import {
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { Fragment, useCallback, useEffect, useMemo } from "react";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";

interface ImproveBleedingRiskFormData {
  IBR_ID: string;
  PROCESS_TIME: Date | null;
  CTIME: Date;
  CUSER: string;
  AGE: string;
  GENDER: string;
  KIDNEY_FAILURE: string;
  ACTIVE_CANCER: string;
  RHEUMATIC_DISEASE: string;
  CENTRAL_VENOUS_CATHETER: string;
  YBU: string;
  LIVER_FAILURE: string;
  PLATELET_COUNT: string;
  BLEEDING_BEFORE_BEDTIME: string;
  GASTROINTESTINAL_ULCER: string;
  CALC_VALUE: string;
}

type RiskFieldKey =
  | "AGE"
  | "GENDER"
  | "KIDNEY_FAILURE"
  | "ACTIVE_CANCER"
  | "RHEUMATIC_DISEASE"
  | "CENTRAL_VENOUS_CATHETER"
  | "YBU"
  | "LIVER_FAILURE"
  | "PLATELET_COUNT"
  | "BLEEDING_BEFORE_BEDTIME"
  | "GASTROINTESTINAL_ULCER";

interface ImproveBleedingRiskProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof ImproveBleedingRiskFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function ImproveBloodRiskCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: ImproveBleedingRiskProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "IMPROVE_BLEEDING_RISK",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof ImproveBleedingRiskFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as ImproveBleedingRiskFormData;

  // Yaş ve cinsiyet otomatik seçimi (Euro Score yapısına benzer)
  useEffect(() => {
    if (!isEditMode && patientData?.data && mcpCacheLoaded) {
      resetForm();
      const patientInfo = patientData.data;
      const sexuality = patientInfo.sexuality;
      const birthdate = patientInfo.birthdate;

      // Yaş hesaplama ve otomatik seçim
      if (birthdate && cardData?.mcpConfig?.AGE) {
        const birthDate = new Date(birthdate);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        const ageMcpId = cardData.mcpConfig.AGE.mcpId;
        const ageDetails = getMCPParameterDetails(ageMcpId);

        if (ageDetails && ageDetails.length > 0) {
          let matchedOption: McParameterDetailResponseDto | null = null;

          // Backend logic'e göre:
          // Yaş >= 40 ve < 85 ise → Yaş 40-84
          // Yaş >= 85 ise → Yaş > 85
          // Yaş < 40 ise → seçim yapılmaz (boş kalır)
          if (age >= 40 && age < 85) {
            matchedOption = ageDetails.find(opt =>
              opt.name?.includes("40") && opt.name?.includes("84")
            ) || null;
          } else if (age >= 85) {
            matchedOption = ageDetails.find(opt =>
              opt.name?.includes(">") && opt.name?.includes("85")
            ) || null;
          }

          if (matchedOption) {
            handleMCPChange("AGE", matchedOption);
          }
        }
      }

      // Cinsiyet otomatik seçim
      if (sexuality && cardData?.mcpConfig?.GENDER) {
        const genderMcpId = cardData.mcpConfig.GENDER.mcpId;
        const genderDetails = getMCPParameterDetails(genderMcpId);

        if (genderDetails && genderDetails.length > 0) {
          let matchedOption: McParameterDetailResponseDto | null = null;

          const genderUpper = sexuality.toUpperCase();

          if (genderUpper === "ERKEK" || genderUpper === "E" || genderUpper === "MALE") {
            matchedOption = genderDetails.find(opt => {
              const optName = opt.name?.toUpperCase() || "";
              return optName.includes("ERKEK") || optName.includes("MALE") || optName === "E";
            }) || null;
          } else if (genderUpper === "KADIN" || genderUpper === "K" || genderUpper === "FEMALE") {
            matchedOption = genderDetails.find(opt => {
              const optName = opt.name?.toUpperCase() || "";
              return optName.includes("KADIN") || optName.includes("FEMALE") || optName === "K";
            }) || null;
          }

          if (matchedOption) {
            handleMCPChange("GENDER", matchedOption);
          }
        }
      }
    } else if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm, patientData, mcpCacheLoaded, cardData, getMCPParameterDetails, handleMCPChange]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "CALC_VALUE") {
        parentHandleChange(key as keyof ImproveBleedingRiskFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  // Diğer risk faktörleri (yaş ve cinsiyet hariç)
  const otherRiskFieldConfigs = useMemo<Array<{ key: RiskFieldKey; label: string }>>(
    () => [
      { key: "KIDNEY_FAILURE", label: t("improve-bleeding-risk.renal-failure") },
      { key: "ACTIVE_CANCER", label: t("improve-bleeding-risk.active-cancer") },
      { key: "RHEUMATIC_DISEASE", label: t("improve-bleeding-risk.rheumatic-cisease") },
      { key: "CENTRAL_VENOUS_CATHETER", label: t("improve-bleeding-risk.central-venous-catheter") },
      { key: "YBU", label: t("improve-bleeding-risk.icu-or-cardiac-icu") },
      { key: "LIVER_FAILURE", label: t("improve-bleeding-risk.liver-failure") },
      { key: "PLATELET_COUNT", label: t("improve-bleeding-risk.platelet-count") },
      { key: "BLEEDING_BEFORE_BEDTIME", label: t("improve-bleeding-risk.bleeding-in-last-months") },
      { key: "GASTROINTESTINAL_ULCER", label: t("improve-bleeding-risk.active-gastrointestinal-ulcer") },
    ],
    [t]
  );

  // Tüm alanlar için skor hesaplama
  const allRiskFieldKeys: RiskFieldKey[] = [
    "AGE", "GENDER", "KIDNEY_FAILURE", "ACTIVE_CANCER", "RHEUMATIC_DISEASE",
    "CENTRAL_VENOUS_CATHETER", "YBU", "LIVER_FAILURE", "PLATELET_COUNT",
    "BLEEDING_BEFORE_BEDTIME", "GASTROINTESTINAL_ULCER"
  ];

  const calculateScore = useCallback(() => {
    return allRiskFieldKeys.reduce((total, key) => {
      return total + getScoreValue(key);
    }, 0);
  }, [getScoreValue]);

  useEffect(() => {
    const totalScore = calculateScore();
    const formattedScore = totalScore.toString();
    if ((typedFormData.CALC_VALUE || "") !== formattedScore) {
      handleChange("CALC_VALUE", formattedScore);
      parentHandleChange("CALC_VALUE", formattedScore);
    }
  }, [calculateScore, handleChange, typedFormData.CALC_VALUE, parentHandleChange]);

  // Sabit input genişliği - tüm inputlar için aynı
  const inputContainerStyle = {
    width: "100%",
    maxWidth: "300px",
    minWidth: "200px",
    paddingRight: "16px",
    marginLeft: "4px",
  };

  // Dropdown'lar için ayrı style (daha sola hizalı)
  const dropdownContainerStyle = {
    width: "100%",
    maxWidth: "300px",
    minWidth: "200px",
    paddingLeft: "16px",
  };

  // MCP field render fonksiyonu (Euro Score'daki gibi)
  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number
  ) => (
    <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {labelKey}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
        <Box sx={dropdownContainerStyle}>
          <ByzMcParameterDetailSelect
            title={t("select")}
            mcpId={mcpId}
            multiple={false}
            handleChange={(val) => {
              if (val === null || val === undefined) {
                handleMCPChange(fieldName, null);
              } else if (!Array.isArray(val)) {
                const selected = val as McParameterDetailResponseDto;
                handleMCPChange(fieldName, selected);
              }
            }}
            value={selectedMCPs[fieldName]}
            sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
            cachedData={getMCPParameterDetails(mcpId)}
            cacheLoaded={mcpCacheLoaded}
            readOnly={!extraAccess}
          />
        </Box>
      </Grid>
    </Grid>
  );


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.IBR_ID || "")}
              loading={isSaving}
              disabled={!extraAccess}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("improve-bleeding-risk.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiDateTimePicker
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                      }
                    }}
                    label={t("datetime")}
                    value={typedFormData.PROCESS_TIME}
                    onChange={(date) => handleChange("PROCESS_TIME", date)}
                    disabled={!extraAccess}
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    color="info"
                    label={t("createdby")}
                    variant="outlined"
                    size="small"
                    value={typedFormData.CUSER}
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("improve-bleeding-risk.existing-risk-factors")}
              </ByzMuiTypography>
            </Grid>

            {/* Yaş - Değiştirilemez (Euro Score'daki gibi) */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("improve-bleeding-risk.age")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    label={t("improve-bleeding-risk.age")}
                    size="small"
                    value={selectedMCPs.AGE?.name || ""}
                    cantChange
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Cinsiyet - Değiştirilemez (Euro Score'daki gibi) */}
            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("improve-bleeding-risk.gender")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    label={t("improve-bleeding-risk.gender")}
                    size="small"
                    value={selectedMCPs.GENDER?.name || ""}
                    cantChange
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Diğer risk faktörleri - Dropdown olarak */}
            {otherRiskFieldConfigs.map(({ key, label }) => {
              const mcpConfig = cardData?.mcpConfig?.[key];
              return (
                <Fragment key={key}>
                  {renderMCPField(key, label, mcpConfig?.mcpId ?? 0)}
                </Fragment>
              );
            })}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between" sx={{ minHeight: "56px" }}>
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0", display: "flex", alignItems: "center", minHeight: "56px" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ display: "flex", alignItems: "center", minHeight: "56px", py: 1 }}>
                <Box sx={inputContainerStyle}>
                  <ByzMuiTextField
                    sx={{ width: "100%", "& .MuiInputBase-root": { height: "40px" } }}
                    label={t("score")}
                    size="small"
                    value={typedFormData.CALC_VALUE || "0"}
                    disabled
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default ImproveBloodRiskCreate;
